import { format, parseISO, subDays } from 'date-fns';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import HttpClient from '../HttpClient';
import HTTPResponse from '../HttpResponse';
import { Errors, ErrorResponse } from './errors';
import {
  ServicesResponse,
  AvailableStore,
  StoresResponse,
  AppointmentResponse,
  PayOrderLinkResponse,
  AvailableServicesInZipResponse,
  AvailableTimeslotsResponse,
  TimeSlotsCapacity,
  ConfirmAppointmentParams,
} from './model';
import { toAvailableService, toAvailableStore } from './utils';
import { isStoreService, ServiceLocation } from '../../helpers';
import { CustomerType } from '../../reducers/user/userTypes';
import { isBusinessOperation, TIMESLOTSTODAY } from '../../helpers/urlHelper';

/* globals CSS_CONFIG */
const { SERVICES, COUNTRY, LANGUAGE } = CSS_CONFIG;

const MAX_REGISTRATION_CLOSING_DAYS_PLUS_ONE = 6;
const MILLISECONDS_REGEX = /\.000/;

const isEmptyResponse = (
  response: AxiosResponse<AvailableServicesInZipResponse[]> | null,
): boolean => {
  return (
    !response || !Array.isArray(response.data) || response.data.length === 0
  );
};

class Appointment {
  private httpTW: HttpClient;

  private httpBoka: HttpClient;

  public constructor(httpTW: HttpClient, httpBoka: HttpClient) {
    this.httpTW = httpTW;
    this.httpBoka = httpBoka;
  }

  private getServices = async (
    excludeIbnPrices: boolean,
    zip: string,
    operation: string,
    stateCode?: string,
  ): Promise<AxiosResponse<AvailableServicesInZipResponse[]>> => {
    const priceTypeURLParam = excludeIbnPrices
      ? '?priceType=Individual'
      : '?priceType=null';
    const stateCodeURLParam =
      SERVICES.getServicesUsingStateCode && stateCode
        ? `&stateCode=${stateCode}`
        : '';
    return this.httpTW.get(
      `/servicesbygroup/${zip}/${operation.toUpperCase()}${priceTypeURLParam}${stateCodeURLParam}`,
    );
  };

  public getAvailableServices = async (
    customerType: CustomerType,
    allowedMemberDiscounts: boolean,
    zip: string,
    operation: string,
    stateCode?: string,
  ): Promise<HTTPResponse<ServicesResponse>> => {
    const isBusiness = isBusinessOperation(operation, CSS_CONFIG);
    const excludeIbnPrices = !isBusiness && customerType === 'Individual';
    try {
      const response = await this.getServices(
        excludeIbnPrices,
        zip,
        operation,
        stateCode,
      );

      if (isEmptyResponse(response)) {
        return new HTTPResponse(
          { services: [] },
          Errors.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const servicesResponse = {
        services: response.data.map(
          toAvailableService(
            operation,
            customerType,
            allowedMemberDiscounts,
            CSS_CONFIG,
          ),
        ),
      };

      return new HTTPResponse(servicesResponse);
    } catch (error) {
      return new HTTPResponse({ services: [] }, Errors.SERVICE_NOT_AVAILABLE);
    }
  };

  public getAvailableTimeslots = async (
    zip: string,
    type: string,
    location: ServiceLocation,
    storeId: string,
    from: string,
    serviceCode: string,
    nbrOfDays?: number,
    language?: string,
  ): Promise<HTTPResponse<AvailableTimeslotsResponse[]>> => {
    const urlParts =
      isStoreService(location) && storeId
        ? [`/services/${zip}/${type}/${storeId}?serviceCode=${serviceCode}`]
        : [`/services/${zip}/${type}?serviceCode=${serviceCode}`];

    const formattedFrom = format(parseISO(from), 'yyyy-MM-dd');
    urlParts.push(`&from=${formattedFrom}`);
    const nbrOfDaysParam = nbrOfDays ? `&noOfDays=${nbrOfDays}` : '';
    urlParts.push(nbrOfDaysParam);

    // Use language selected in language picker if available
    const config: AxiosRequestConfig = language
      ? {
          headers: {
            'country-Language': `${COUNTRY}-${language}`,
          },
        }
      : {};

    try {
      const result = await this.httpTW.get<AvailableTimeslotsResponse[]>(
        urlParts.join(''),
        config,
      );
      return new HTTPResponse(result.data);
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response) {
        const error =
          Object.values(Errors).indexOf(err.response.status) > -1
            ? err.response.status
            : Errors.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse([], error);
      }
      return new HTTPResponse([], Errors.SERVICE_NOT_AVAILABLE);
    }
  };

  public getAvailableStores = async (
    zip: string,
    serviceProductId: string,
  ): Promise<HTTPResponse<StoresResponse>> => {
    try {
      const url = `/locations/${zip}/${serviceProductId}`;
      const result = await this.httpTW.get(url);

      if (result.status < 200 || result.status >= 300) {
        const error =
          Object.values(Errors).indexOf(result.status) > -1
            ? result.status
            : Errors.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse({ stores: [] }, error);
      }
      if (!Array.isArray(result.data) || result.data.length === 0) {
        return new HTTPResponse(
          { stores: [] },
          Errors.SERVICE_NO_LONGER_POSSIBLE,
        );
      }

      const stores: AvailableStore[] = result.data.map(toAvailableStore);
      return new HTTPResponse({ stores });
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response) {
        const error =
          Object.values(Errors).indexOf(err.response.status) > -1
            ? err.response.status
            : Errors.SERVICE_NOT_AVAILABLE;
        return new HTTPResponse({ stores: [] }, error);
      }
      return new HTTPResponse({ stores: [] }, Errors.SERVICE_NOT_AVAILABLE);
    }
  };

  public confirmAppointment = async (
    {
      zipCode,
      serviceId,
      storeId,
      timeSlotId,
      from,
      serviceCode,
      payload,
      selectedLanguage,
    }: ConfirmAppointmentParams,
    config: AxiosRequestConfig,
  ): Promise<HTTPResponse<AppointmentResponse>> => {
    try {
      let url = `/services/${zipCode}/${serviceId}/${storeId}/${timeSlotId}?serviceCode=${serviceCode}`;

      const formattedFrom = format(
        subDays(parseISO(from), MAX_REGISTRATION_CLOSING_DAYS_PLUS_ONE),
        'yyyy-MM-dd',
      );
      url = `${url}&from=${formattedFrom}`;

      const mergedConfig = {
        ...config,
        headers: {
          ...config.headers,
          ...(selectedLanguage && {
            'country-Language': `${COUNTRY}-${selectedLanguage}`,
          }),
        },
      };

      const { data, status } = await this.httpTW.post<AppointmentResponse>(
        url,
        payload,
        mergedConfig,
      );

      // 208 is a successful request; but we want to show the user an error.
      // So we return early with an "error object" so that we can derive the error status from the returned object.
      if (status === Errors.ALREADY_REPORTED) {
        return new HTTPResponse(data, {
          status: Errors.ALREADY_REPORTED,
          data: { ...data },
        });
      }

      if (status === Errors.FORBIDDEN) {
        return new HTTPResponse(data, {
          status: Errors.FORBIDDEN,
          data: { ...data },
        });
      }

      return new HTTPResponse(data);
    } catch (err: unknown) {
      const appointmentResponse: AppointmentResponse = {
        orderNo: '',
        payOrderLink: '',
        questionsToken: '',
        payOrderLinkEnabled: true,
      };
      if (err instanceof AxiosError && err.response) {
        const error: ErrorResponse = {
          status:
            Object.values(Errors).indexOf(err.response.status) > -1
              ? err.response.status
              : Errors.SERVICE_NOT_AVAILABLE,
          data: err.response.data || {},
        };
        return new HTTPResponse(appointmentResponse, error);
      }
      return new HTTPResponse(
        appointmentResponse,
        Errors.SERVICE_NOT_AVAILABLE,
      );
    }
  };

  public async getPayOrderLink(
    orderNumber: string,
    email: string,
  ): Promise<string> {
    const encodedEmail = encodeURIComponent(email);
    try {
      const response: AxiosResponse<PayOrderLinkResponse> =
        await this.httpTW.get(
          `/payorderlink?orderNumber=${orderNumber}&email=${encodedEmail}`,
        );
      const link = response?.data?.link ?? '';
      return link;
    } catch (error) {
      return '';
    }
  }

  public getIANATimeZone = async (
    postalCode: string,
    serviceProductId: string,
  ): Promise<string> => {
    try {
      const response: AxiosResponse<string> = await this.httpBoka.get(
        `/services/timezone/${postalCode}/${serviceProductId}`,
      );

      const timeZone = response?.data;
      return timeZone;
    } catch (error) {
      return '';
    }
  };

  public getTimeslotsToday = async (
    zip: string,
    storeId: string,
    serviceCode: string,
    serviceId: string,
  ): Promise<AvailableTimeslotsResponse[]> => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await this.httpTW.get(
        `services/${zip}/${serviceId}/${storeId}?serviceCode=${serviceCode}&from=${today}&source=${TIMESLOTSTODAY}`,
      );
      if (Array.isArray(response.data)) {
        return response.data
          .filter((item) => item.from.startsWith(today))
          .slice(0, 9);
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  public getTimeSlotCapacity = async (
    postalCodeForLocation: string,
    serviceProductId: string,
    from: string,
    isTimeInBrowserSettings: boolean,
    language?: string,
  ): Promise<TimeSlotsCapacity[]> => {
    const ianaTimeZone = isTimeInBrowserSettings
      ? `&ianaTimeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
      : '';

    // Use language selected in language picker if available
    const config: AxiosRequestConfig = language
      ? {
          headers: {
            'country-Language': `${COUNTRY}-${language}`,
          },
        }
      : {};

    try {
      const urlParts = [
        '/timewindow/capacity',
        `?postalCode=${postalCodeForLocation}`,
        `&serviceProductId=${serviceProductId}`,
        `&countryCode=${COUNTRY}`,
        `&language=${language || LANGUAGE}`,
        `&startDateTimeUTC=${from.replace(MILLISECONDS_REGEX, '')}`,
        `${ianaTimeZone}`,
      ];
      const response: AxiosResponse<TimeSlotsCapacity[]> =
        await this.httpBoka.get(urlParts.join(''), config);
      return response.data;
    } catch (error) {
      return [];
    }
  };
}

export default Appointment;
